import React, { useState, useEffect, useRef } from 'react';
import { portfolioProjects } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProjectModal from '../components/ProjectModal';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const projectRefs = useRef([]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const categories = ['All', ...new Set(portfolioProjects.map(p => p.category))];

  const filteredProjects = selectedCategory === 'All' 
    ? portfolioProjects 
    : portfolioProjects.filter(p => p.category === selectedCategory);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = entry.target.dataset.index;
            setVisibleProjects((prev) => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.1 }
    );

    projectRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [filteredProjects]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    // Reset selected project after modal closes
    setTimeout(() => {
      setSelectedProject(null);
      setCurrentImageIndex(0);
    }, 300);
  };

  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  const handleSetImageIndex = (index) => {
    setCurrentImageIndex(index);
  };

  const handleContactClick = () => {
    closeModal();
    // Navigate to contact section
    if (window.location.pathname !== '/') {
      window.location.href = '/#contact';
    } else {
      const footer = document.querySelector('footer');
      if (footer) {
        footer.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="bg-black min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            OUR PORTFOLIO
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Explore our collection of innovative design solutions that have transformed industries and delighted users worldwide.
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Filter className="text-white" size={20} />
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'default' : 'outline'}
              className={`transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-white text-black hover:bg-gray-200'
                  : 'bg-transparent border-white/20 text-white hover:bg-white/10'
              }`}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <Card
              key={project.id}
              ref={(el) => (projectRefs.current[index] = el)}
              data-index={index}
              onClick={() => handleProjectClick(project)}
              className={`bg-white border-none overflow-hidden group cursor-pointer transition-all duration-1000 transform hover:scale-105 ${
                visibleProjects.includes(String(index))
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${(index % 3) * 150}ms` }}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <Badge className="bg-white text-black mb-2">{project.category}</Badge>
                  <p className="text-white text-sm">View Project</p>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-black">
                    {project.title}
                  </h3>
                  <span className="text-sm text-gray-500">{project.year}</span>
                </div>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs border-black/20">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 mt-24">
        <div className="bg-white rounded-sm p-12 text-center">
          <h2 className="text-4xl font-bold text-black mb-4">
            Have a Project in Mind?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's collaborate and create something exceptional together. Our team is ready to bring your vision to life.
          </p>
          <Button size="lg" className="bg-black text-white hover:bg-gray-800 transition-all duration-300">
            Get Started
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>

      {/* Project Modal - Only render when selectedProject exists */}
      {selectedProject && (
        <ProjectModal 
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={closeModal}
          currentImageIndex={currentImageIndex}
          onNextImage={nextImage}
          onPrevImage={prevImage}
          onSetImageIndex={handleSetImageIndex}
          onContactClick={handleContactClick}
        />
      )}
    </div>
  );
};

export default Portfolio;